import { PERSONAL_SOURCES, type PersonalSource } from "@/api/client/personal-sources"

export const PERSONAL_SOURCE_CALLBACK_PATH = "/auth/personal-sources/callback"
export const PERSONAL_SOURCE_SETTINGS_PATH = "/settings/personal-sources"
export const PERSONAL_SOURCE_RETURN_KEY = "personal-source-return"

/**
 * The source a consent was started for, read from the OAuth `state`.
 *
 * The backend mints `state` as `<source>.<random>` so the single callback page
 * knows which route to complete. Anything else (an unknown prefix, no separator)
 * is refused: the page never guesses a source and never forwards an arbitrary
 * path segment to the API.
 */
export function personalSourceFromState(state: string | null | undefined): PersonalSource | null {
  if (!state) return null
  const separator = state.indexOf(".")
  if (separator <= 0) return null
  const candidate = state.slice(0, separator)
  return (PERSONAL_SOURCES as readonly string[]).includes(candidate)
    ? (candidate as PersonalSource)
    : null
}

/** Never follow a return URL outside this app, nor loop back into the callback. */
export function safePersonalSourceReturnPath(value: string | null, origin: string): string {
  if (!value) return PERSONAL_SOURCE_SETTINGS_PATH
  try {
    const target = new URL(value, origin)
    if (target.origin !== origin || target.pathname === PERSONAL_SOURCE_CALLBACK_PATH) {
      return PERSONAL_SOURCE_SETTINGS_PATH
    }
    return `${target.pathname}${target.search}${target.hash}`
  } catch {
    return PERSONAL_SOURCE_SETTINGS_PATH
  }
}

let pendingAuthorizationResponse: string | undefined

/**
 * Isolate the backend-owned consent code from the app's own MSAL redirect
 * handler: keep only the four OAuth fields, in memory, and clear the URL.
 */
export function capturePersonalSourceAuthorizationResponse(): void {
  if (
    typeof window === "undefined" ||
    location.pathname !== PERSONAL_SOURCE_CALLBACK_PATH ||
    !location.search
  ) {
    return
  }
  const input = new URLSearchParams(location.search)
  const response = new URLSearchParams()
  for (const key of ["code", "state", "error", "error_description"]) {
    const value = input.get(key)
    if (value) response.set(key, value)
  }
  pendingAuthorizationResponse = response.toString()
  history.replaceState(history.state, "", location.pathname)
}

export function takePersonalSourceAuthorizationResponse(): string | undefined {
  const response = pendingAuthorizationResponse
  pendingAuthorizationResponse = undefined
  return response
}
